import type { ApplicationSubmission, CareerPageSettings, PublicJob } from '@recruitai/shared';

const API_URL = import.meta.env.VITE_API_URL || 'https://o7pkyged3j.execute-api.us-east-1.amazonaws.com';

// Response types for career page endpoints
export interface CareerPageResponse {
  organization: {
    name: string;
    slug: string;
    logoUrl: string | null;
    websiteUrl: string | null;
    careerPage: CareerPageSettings;
  };
  jobs: PublicJob[];
}

export interface PublicJobResponse {
  job: PublicJob;
  organization: {
    name: string;
    logoUrl: string | null;
    primaryColor: string;
  };
}

export interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface SubmitApplicationResponse {
  message: string;
  applicationId: string;
}

async function publicRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    const err = new Error(data.error?.message || 'Request failed');
    (err as any).code = data.error?.code;
    (err as any).statusCode = response.status;
    throw err;
  }
  return data.data;
}

export const careerPageApi = {
  getCareerPage: (orgSlug: string) =>
    publicRequest<CareerPageResponse>(`/public/careers/${orgSlug}`),

  getPublicJob: (orgSlug: string, jobId: string) =>
    publicRequest<PublicJobResponse>(`/public/careers/${orgSlug}/jobs/${jobId}`),

  getUploadUrl: (orgSlug: string, jobId: string, fileName: string, fileType: string) =>
    publicRequest<UploadUrlResponse>(`/public/careers/${orgSlug}/jobs/${jobId}/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ fileName, fileType }),
    }),

  submitApplication: (orgSlug: string, jobId: string, data: ApplicationSubmission) =>
    publicRequest<SubmitApplicationResponse>(`/public/careers/${orgSlug}/jobs/${jobId}/apply`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};
